import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, Shield, Copy, Clock, KeyRound } from "lucide-react";

export function AdminsDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ email: "", name: "", role: "admin" });
  const [saving, setSaving] = useState(false);
  const [tempCred, setTempCred] = useState(null); // { email, temp_password }

  const load = async () => {
    const { data } = await api.get("/admins");
    setAdmins(data);
  };
  useEffect(() => { if (open) { load(); setTempCred(null); } }, [open]);

  const create = async () => {
    if (!form.email.trim()) return toast.error("Email is required");
    setSaving(true);
    try {
      const { data } = await api.post("/admins", form);
      setTempCred({ email: data.email, temp_password: data.temp_password });
      toast.success("Admin added — share the temp password");
      setForm({ email: "", name: "", role: "admin" });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not add admin");
    } finally {
      setSaving(false);
    }
  };

  const copyCred = () => {
    navigator.clipboard?.writeText(`Email: ${tempCred.email}\nTemp password: ${tempCred.temp_password}`);
    toast.success("Copied to clipboard");
  };

  const resetPassword = async (id, email) => {
    if (!window.confirm(`Force a password reset for ${email}? A new temp password will be generated.`)) return;
    try {
      const { data } = await api.post(`/admins/${id}/reset-password`);
      setTempCred({ email: data.email, temp_password: data.temp_password });
      toast.success("Temp password generated");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reset password");
    }
  };

  const changeRole = async (id, role) => {
    try {
      await api.patch(`/admins/${id}/role`, { role });
      await load();
      toast.success("Role updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update role");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try {
      await api.delete(`/admins/${id}`);
      await load();
      toast.success("Admin removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove admin");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border border-white/15 rounded-none max-w-4xl" data-testid="admins-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl flex items-center gap-2"><ShieldCheck size={20} /> Manage Admins</DialogTitle>
          <DialogDescription className="sr-only">Add admins, change their roles, or remove them. Head admins have full control.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 py-2">
          {/* Existing admins */}
          <div className="space-y-2">
            <p className="text-xs font-mono uppercase tracking-widest text-zinc-400">Current Admins</p>
            {admins.map((a) => (
            <div key={a.id} className="flex items-center gap-3 border border-white/10 bg-[#121212] px-3 py-2.5" data-testid={`admin-row-${a.id}`}>
              <div className="flex-grow min-w-0">
                <p className="text-sm truncate flex items-center gap-2">
                  {a.name || a.email}
                  {a.must_change_password && (
                    <span className="flex items-center gap-1 text-[10px] text-amber-400 border border-amber-400/40 px-1.5 py-0.5" data-testid={`admin-pending-${a.id}`}>
                      <Clock size={10} /> Pending
                    </span>
                  )}
                </p>
                <p className="text-xs text-zinc-500 font-mono truncate">{a.email}{a.id === user.id ? " (you)" : ""}</p>
              </div>
              <Select value={a.role} onValueChange={(v) => changeRole(a.id, v)}>
                <SelectTrigger data-testid={`admin-role-${a.id}`} className="w-32 bg-transparent border-white/15 rounded-none h-9 focus:ring-[#FF3B30] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/15 rounded-none text-white">
                  <SelectItem value="admin"><span className="flex items-center gap-2"><Shield size={13} /> Admin</span></SelectItem>
                  <SelectItem value="head_admin"><span className="flex items-center gap-2"><ShieldCheck size={13} /> Head</span></SelectItem>
                </SelectContent>
              </Select>
              <button data-testid={`reset-admin-${a.id}`} onClick={() => resetPassword(a.id, a.email)} title="Force password reset"
                className="text-zinc-400 hover:text-amber-400 p-2 transition-colors">
                <KeyRound size={15} />
              </button>
              <button data-testid={`remove-admin-${a.id}`} onClick={() => remove(a.id)} disabled={a.id === user.id}
                className="text-zinc-400 hover:text-red-500 p-2 disabled:opacity-30 disabled:hover:text-zinc-400">
                <Trash2 size={15} />
              </button>
            </div>
          ))}
        </div>

        <div className="space-y-3 pt-3 border-t border-white/10">
          <p className="text-xs font-mono uppercase tracking-widest text-[#FF3B30] flex items-center gap-2"><UserPlus size={14} /> Add New Admin</p>

          {tempCred && (
            <div className="border border-[#FF3B30]/50 bg-[#FF3B30]/10 p-3 space-y-2" data-testid="temp-cred-box">
              <p className="text-xs font-mono uppercase tracking-widest text-[#FF3B30]">Temp Password Generated</p>
              <p className="text-xs text-zinc-300 font-mono break-all">{tempCred.email}</p>
              <p className="text-lg font-mono font-bold tracking-wide" data-testid="temp-password-value">{tempCred.temp_password}</p>
              <p className="text-[11px] text-zinc-400">Share this with the new admin. They'll be asked to set their own password on first sign in.</p>
              <button data-testid="copy-temp-cred-btn" onClick={copyCred}
                className="flex items-center gap-2 border border-white/20 hover:border-[#FF3B30] hover:text-[#FF3B30] px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-colors">
                <Copy size={13} /> Copy
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Name</Label>
              <Input data-testid="new-admin-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" placeholder="Jane" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-admin-role" className="bg-transparent border-white/15 rounded-none focus:ring-[#FF3B30]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#121212] border-white/15 rounded-none text-white">
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="head_admin">Head Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Email</Label>
            <Input data-testid="new-admin-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" placeholder="jane@troytested.com" />
          </div>
          <button data-testid="create-admin-btn" onClick={create} disabled={saving}
            className="w-full bg-white text-black font-bold py-3 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
            {saving ? "Adding..." : "Add Admin & Generate Password"}
          </button>
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
